/**
 * 匯入批次 (/roster/batches, boards/Batches.dc.html) — admin+. Every import wizard commit is one
 * batch (GET /api/whitelist/import-batches). The name filters the roster by that batch; 匯出這批
 * is the roster CSV export with batch=<id>. Imports are written in ≤200-row segments; a batch whose
 * writing stopped shows 「未完成(第 x / y 段)」 and its creator can 繼續 it to the end (the Worker
 * only lets the creator continue a batch).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { errMsg } from '@/components/Dialogs';
import { useAuth } from '@/contexts/AuthContext';
import { usePoll } from '@/lib/usePoll';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { pendingLabel, runImport, writingLabel } from '@/lib/importRun';
import type { ImportBatch } from '@/types';

export function RosterBatches() {
  const list = usePoll<ImportBatch[]>(() => api.listImportBatches(), 60_000);
  const rows = list.data ?? [];
  const { email } = useAuth();
  const [running, setRunning] = useState<{ id: string; label: string } | null>(null);
  const [runErr, setRunErr] = useState<{ id: string; msg: string } | null>(null);

  const resume = async (b: ImportBatch) => {
    setRunning({ id: b.id, label: '寫入中…' }); setRunErr(null);
    try {
      await runImport(
        () => api.importContinue(b.id),
        (id) => api.importContinue(id),
        (p) => setRunning({ id: b.id, label: writingLabel(p.last, true) }),
      );
    } catch (e) {
      setRunErr({ id: b.id, msg: errMsg(e) });
    } finally {
      setRunning(null);
      list.refresh();
    }
  };
  return (
    <div className="v2-page">
      <div className="v2-head" style={{ alignItems: 'center' }}>
        <div className="titles"><h1 className="v2-title">匯入批次</h1></div>
        <Link className="btn btn-primary" to="/roster/import">新的匯入…</Link>
      </div>
      <p className="v2-sub" style={{ margin: 0 }}>
        每次匯入成為一個批次。點批次名稱會在名單頁以「匯入批次」篩選;升級前的 CSV 匯入沒有批次紀錄。
      </p>
      <section className="panel flush">
        {Boolean(list.error) && !list.data && <div className="error-line" style={{ padding: 'var(--space-3) var(--space-4)' }}>載入失敗:{errMsg(list.error)}</div>}
        <div className="tscroll">
          <table className="v2-table">
            <thead>
              <tr>
                <th>批次名稱</th>
                <th>匯入時間</th>
                <th>操作者</th>
                <th>檔案列數</th>
                <th>新增</th>
                <th>更新</th>
                <th>略過</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="empty-row"><td colSpan={8}>{list.loading ? '載入中…' : '還沒有匯入批次'}</td></tr>
              ) : (
                rows.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <Link className="link" to={`/roster?batch=${encodeURIComponent(b.id)}`}>{b.name}</Link>
                      {b.file_name && <div className="muted-note">{b.file_name}</div>}
                    </td>
                    <td className="muted num">{fmtDate(b.created_at)}</td>
                    <td className="muted">{b.created_by}</td>
                    <td className="num">{b.row_count}</td>
                    <td className="num">{b.added}</td>
                    <td className="num" title={b.restored ? `含復原 ${b.restored} 台` : undefined}>
                      {b.updated}{b.restored ? ` (+${b.restored} 復原)` : ''}
                    </td>
                    <td className="num">{b.skipped}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {b.status === 'pending' && (
                        <span className="muted-note" style={{ marginRight: 'var(--space-3)' }}>
                          {running?.id === b.id ? running.label : pendingLabel(b)}
                          {b.created_by === email && running?.id !== b.id && (
                            <button type="button" className="link" style={{ marginLeft: 'var(--space-2)' }} disabled={running !== null} onClick={() => void resume(b)}>
                              繼續
                            </button>
                          )}
                        </span>
                      )}
                      {b.status === 'failed' && <span className="muted-note" style={{ marginRight: 'var(--space-3)' }} title="未完成,暫存已過期(保留 24 小時);已寫入的列會保留">已失效</span>}
                      <a className="link" href={api.rosterExportUrl({ batch: b.id })} download>匯出這批</a>
                      {runErr?.id === b.id && <div className="error-line" role="alert">繼續失敗:{runErr.msg}</div>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
